
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Answer
 *
 * A user's response to a custom Question, linked to an entity (lead, matter, contact, or other) via entityId/entityType. The value is stored as a string and interpreted according to the question's answerType (Text, Date, Money, Contact(s), etc.); sensitive answers are stored in an encrypted column instead.
 */
class Answer extends Base implements EntityType {
    answerId: uuid;
    questionId: uuid;
    value?: string | null;
    encryptedValue?: string | null;
    position?: number | null;

    /**
     * Create a new Answer, with an (optional) JSON object or Answer class
     *
     * @param {Object|Answer} doc - Can be either a simple JSON object or a Answer class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Answer | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            answerId: genUUID(),
            questionId: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'answer';
        // @ts-ignore
        this.answerId = doc._answerId || doc.answerId || doc.answerid || genUUID();
        // @ts-ignore
        this.questionId = doc._questionId || doc.questionId || doc.questionid || null;
        // @ts-ignore
        this.value = doc._value || doc.value || null;
        // @ts-ignore
        this.encryptedValue = doc._encryptedValue || doc.encryptedValue || doc.encryptedvalue || null;
        // @ts-ignore
        this.position = util.coalesce(util.parseInt(doc._position), util.parseInt(doc.position), null);

    }
    /**
     * @returns {uuid} This is a generic pointer to the answerId across all entity types
     */
    get id(): uuid {
        return this.answerId;
    }


    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this answer"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.answerId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied Answer entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Answer[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'answer',
            table: 'ANSWER',
            comment: `A user's response to a custom Question, linked to an entity (lead, matter, contact, or other) via entityId/entityType. The value is stored as a string and interpreted according to the question's answerType (Text, Date, Money, Contact(s), etc.); sensitive answers are stored in an encrypted column instead.`,
            fields: {
                answerId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this answer'
                },
                questionId: {
                    type: 'uuid',
                    entityType: 'question',
                    required: true,
                    comment: 'The base question'
                },
                value: {
                    type: 'string',
                    comment: `The answer, stored as a string; how it's interpreted depends on the related question's answer type`
                },
                encryptedValue: {
                    type: 'string',
                    encrypted: true,
                    comment: 'If the question requires encryption, we store the answer in an encrypted field'
                },
                // Transients
                position: {
                    type: 'int',
                    transient: true,
                    comment: 'Temporary position based on order of Question'
                }
            },
            maps: [{
                    name: 'relatedTo',
                    keysFn: t => t.entityId,
                    presentFn: (t, k) => t.entityId === k
                },
                {
                    name: 'questionId',
                    keysFn: t => t.questionId,
                    presentFn: (t, k) => t.questionId === k
                },
            ],
        });
    }

}
export default Answer;
